<template>
  <Dialogue
    :is-visible="isVisible"
    is-fullscreen
  >
    <v-card class="nbiot-temp-dialog">
      <div class="nbiot-temp-dialog__header">
        <div class="nbiot-temp-dialog__header-side">
          <div
            class="nbiot-temp-dialog__unit-toggle"
            role="radiogroup"
            aria-label="Temperature unit"
          >
            <button
              class="nbiot-temp-dialog__unit-btn"
              :class="{ 'nbiot-temp-dialog__unit-btn--active': isCelsius }"
              :aria-pressed="String(isCelsius)"
              @click="setTemperatureUnit(true)"
            >
              °C
            </button>
            <button
              class="nbiot-temp-dialog__unit-btn"
              :class="{ 'nbiot-temp-dialog__unit-btn--active': !isCelsius }"
              :aria-pressed="String(!isCelsius)"
              @click="setTemperatureUnit(false)"
            >
              °F
            </button>
          </div>
        </div>
        <h2 class="nbiot-temp-dialog__title">
          Temperature
        </h2>
        <div class="nbiot-temp-dialog__header-side nbiot-temp-dialog__header-side--end">
          <button
            class="nbiot-temp-dialog__close-btn"
            aria-label="Close"
            @click="$emit('close')"
          >
            <v-icon
              color="#6B7273"
              size="24"
            >
              mdi-close
            </v-icon>
          </button>
        </div>
      </div>
      <div class="nbiot-temp-dialog__body">
        <div
          v-if="!isLoading && !hasError && hasData"
          class="nbiot-temp-dialog__chart-area"
        >
          <NbIotTemperatureChart
            :asset-name="assetName"
            :ble-id="bleId"
            :temperature-data="temperatureData"
            :asset-space-data="spaceData"
            :is-full-screen="true"
            :full-screen-window-height="chartAreaHeight"
            :include-download="includeDownload"
            :thresholds="thresholds"
          />
        </div>
        <div
          v-if="!isLoading && !hasError && !hasData"
          class="nbiot-temp-dialog__empty"
        >
          No data
        </div>
      </div>
    </v-card>
    <resize-observer @notify="setFullScreenHeight" />
  </Dialogue>
</template>
<script>
import Dialogue from '../../atoms/dialogue/dialogue';
import NbIotTemperatureChart from '../../atoms/charts/nb-iot-temperature-chart';

const HEADER_HEIGHT = 52;

export default {
  components: {
    Dialogue,
    NbIotTemperatureChart,
  },
  props: {
    isVisible: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
    hasData: { type: Boolean, default: false },
    assetName: { type: String, default: '' },
    bleId: { type: Number, default: 0 },
    temperatureData: { type: Array, default: () => [] },
    spaceData: { type: Array, default: () => [] },
    thresholds: { type: Array, default: () => ([]) },
    includeDownload: { type: Boolean, default: false },
  },
  data() {
    return {
      fullScreenWindowHeight: 0,
    };
  },
  computed: {
    isCelsius() {
      return this.$store.getters['user/isCelsiusTemperatureUnits'];
    },
    chartAreaHeight() {
      return this.fullScreenWindowHeight - HEADER_HEIGHT;
    },
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.fullScreenWindowHeight = window.innerHeight;
      }
    },
  },
  methods: {
    setFullScreenHeight() {
      this.fullScreenWindowHeight = window.innerHeight;
    },
    setTemperatureUnit(isCelsius) {
      this.$store.dispatch('user/setTemperatureUnits', isCelsius);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.nbiot-temp-dialog {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.625rem 1rem;
    border-bottom: 1px solid $gray3;
    flex-shrink: 0;
    background-color: #fff;
  }

  &__header-side {
    flex: 0 0 auto;
    min-width: 4.5rem;
    display: flex;
    align-items: center;

    &--end {
      justify-content: flex-end;
    }
  }

  &__title {
    flex: 1 1 auto;
    text-align: center;
    font-size: 1rem;
    font-weight: 600;
    color: $gray1;
    margin: 0;
    line-height: 1.3;
  }

  &__unit-toggle {
    display: inline-flex;
    border: 1px solid $gray3;
    border-radius: 0.25rem;
    overflow: hidden;
  }

  &__unit-btn {
    all: unset;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.1875rem 0.5rem;
    cursor: pointer;
    color: $gray3;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(&--active) {
      background-color: rgba(69, 190, 193, 0.1);
      color: $gray2;
    }

    &--active {
      background-color: $tealBright;
      color: #fff;
    }
  }

  &__close-btn {
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  &__body {
    flex: 1 1 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  &__chart-area {
    flex: 1 1 auto;
    overflow: hidden;
    margin: 0.25rem;
    border: 1px solid $gray3;
    border-radius: 0.5rem;
    background-color: #fafbfb;

    @media (min-width: 960px) {
      margin: 0.75rem 1rem;
    }
  }

  &__empty {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $gray2;
    font-size: 0.875rem;
  }
}
</style>
